import { Flex } from '@chakra-ui/react';
import { Tag } from '@kula-ai/components';
import { capitalizeFirstLetter } from '@/utils/commonHelpers';
import type { CandidateSummary } from '../../../types';

type ChipVariant = 'neutral' | 'success' | 'warning';

// A single pill: its label and tone.
interface Chip {
  id: string;
  label: string;
  variant: ChipVariant;
}

const jobsPhrase = (n: number): string => `${n} ${n === 1 ? 'job' : 'jobs'}`;

// Derive the pill row from a candidate. Explicit `tags` (backend-authored)
// render as-is; otherwise we derive match / status / job-count chips.
const buildChips = (c: CandidateSummary): Chip[] => {
  if (c.tags.length > 0) {
    return c.tags.map((t, i) => ({
      id: `tag-${i}-${t}`,
      label: t,
      variant: 'neutral',
    }));
  }

  const chips: Chip[] = [];

  if (c.matchBadge) {
    chips.push({
      id: 'match',
      label: c.matchBadge,
      variant: /exact|strong/i.test(c.matchBadge) ? 'success' : 'neutral',
    });
  }

  // Engagement badges cover status/activity when present, so skip the derived
  // status + job-count chips to avoid duplicating the same signal.
  if (c.badges && c.badges.length > 0) return chips;

  Array.from(new Set(c.applicationStatuses ?? []))
    .map((s) => capitalizeFirstLetter(s))
    .forEach((status) =>
      chips.push({
        id: `status-${status}`,
        label: status,
        variant: 'neutral',
      })
    );

  if (typeof c.jobCount === 'number' && c.jobCount > 0) {
    chips.push({
      id: 'jobs',
      label: `On ${jobsPhrase(c.jobCount)}`,
      variant: 'neutral',
    });
  }

  return chips;
};

// Nexus engagement badge tone → kula-ai Tag variant.
const TONE_TO_VARIANT: Record<string, ChipVariant> = {
  positive: 'success',
  warning: 'warning',
  info: 'neutral',
};

export const CandidateTags = ({
  candidate,
}: {
  candidate: CandidateSummary;
}) => {
  const chips = buildChips(candidate);
  const badges = candidate.badges ?? [];
  if (chips.length === 0 && badges.length === 0) return null;

  return (
    <Flex
      wrap="wrap"
      align="center"
      gap="6px"
      mt="10px"
      data-test-id="agentforge-candidate-tags"
    >
      {chips.map((chip) => (
        <Tag
          key={chip.id}
          variant={chip.variant}
          size="sm"
          label={chip.label}
        />
      ))}
      {badges.map((b) => (
        <Tag
          key={b.key || b.label}
          size="sm"
          variant={TONE_TO_VARIANT[b.tone ?? ''] ?? 'neutral'}
          label={b.label}
        />
      ))}
    </Flex>
  );
};
